// Acesso aos dados. As telas leem do aparelho (funciona sem sinal) e gravam
// primeiro no aparelho, na fila de envio. Com sinal, a gravação espera o
// servidor responder (até alguns segundos): se ele recusar (placa repetida,
// regra do banco…), a gravação é desfeita e o erro aparece no formulário, como
// antes. Sem sinal, fica na fila e sobe depois; recusas posteriores aparecem
// em "Alterações não enviadas". Toda leitura filtra pela empresa atual e
// ignora o que está na lixeira (deleted_at). A segurança está na RLS do banco.
//
// Só com internet: criar a conta e ativar, trocar ou retirar placa (mexem na
// cobrança e passam por funções do servidor).
import { VERSAO_TERMOS } from './config.js';
import { novoCodigo, sha256Hex } from './lib/assinatura.js';
import { extensao } from './lib/fotos.js';
import { itensDoAcerto } from './lib/acerto.js';
import { itensAPagar } from './lib/despesas.js';
import { comContas } from './lib/fretes.js';
import { novoId } from './lib/ids.js';
import * as armazem from './local/armazem.js';
import { TABELAS } from './local/banco.js';
import {
  atualizarResumo, ehErroDeRede, estado, exigirConexao, gerarContasFixasLogo, puxarAgora, sincronizar, sincronizarLogo,
} from './local/sincronia.js';
import { atualizarEmpresa as atualizarEmpresaNaSessao, sessao } from './sessao.js';
import { comPrazo, executar, supabase } from './supabase.js';

// Quanto a gravação espera o servidor quando há sinal. Passou disso (sinal
// fraco), segue: fica na fila e vai por trás.
const ESPERA_SERVIDOR_MS = 8000;

const empresaId = () => sessao.empresa.id;
const agora = () => new Date().toISOString();

// Valores padrão do banco, para o registro criado sem sinal já aparecer certo.
const PADROES = {
  veiculos: { ativo: true },
  colaboradores: { ativo: true },
  clientes: { ativo: true, prazo_saldo_dias: 30 },
  regras_remuneracao: { inclui_estadia: false },
  viagens: { status: 'aberta' },
  fretes: {
    estadia_valor: 0, pedagio_modo: 'sem', pedagio_valor: 0, desc_inss: 0, desc_sest_senat: 0, desc_irrf: 0,
    desc_outros: 0, agenciamento_valor: 0, adto_valor: 0, comprovante_status: 'nao_precisa',
  },
  lancamentos: { condicao: 'a_vista', origem: 'app', hodometro_confirmado: false },
  contas_fixas: { ativo: true },
  anexos: { origem: 'app' },
  oficinas: { ativo: true, whatsapp: false, tipos: [] },
};

// O que o banco calcula e o aparelho já mostra antes de o servidor responder.
const CONTAS = {
  fretes: comContas,
};
const comContasDa = (tabela, linha) => (CONTAS[tabela] ? CONTAS[tabela](linha) : linha);

const porTexto = (campo) => (a, b) => String(a[campo] ?? '').localeCompare(String(b[campo] ?? ''), 'pt-BR', { sensitivity: 'base' });
const decrescente = (campo) => (a, b) => (a[campo] < b[campo] ? 1 : a[campo] > b[campo] ? -1 : 0);
const crescente = (campo) => (a, b) => (a[campo] < b[campo] ? -1 : a[campo] > b[campo] ? 1 : 0);
// Mais recente primeiro; no mesmo dia, o lançado por último primeiro.
const recentes = (campo) => (a, b) => decrescente(campo)(a, b) || decrescente('created_at')(a, b);

async function listar(tabela, ordem = null) {
  const linhas = (await armazem.todas(tabela, empresaId())).filter((l) => !l.deleted_at);
  return ordem ? linhas.sort(ordem) : linhas;
}

async function obter(tabela, id) {
  const linha = await armazem.uma(tabela, id);
  return linha && !linha.deleted_at && linha.empresa_id === empresaId() ? linha : null;
}

// Grava no aparelho e, com sinal, espera o servidor. Devolve a linha como
// ficou (a do servidor, se chegou lá; senão, a do aparelho).
// Com `esperarServidor: false`, grava e segue sem esperar a resposta (para
// quando a tela precisa agir na hora, como abrir o WhatsApp).
async function salvar(tabela, linha, operacao, anterior, { esperarServidor = true } = {}) {
  const tinhaPendencia = (await armazem.situacoes(tabela)).has(linha.id);
  const gravada = await armazem.gravar(tabela, linha, operacao);
  if (!estado.online || !esperarServidor) {
    sincronizarLogo();
    return gravada;
  }
  // Se a sincronia falhar por outro motivo, a gravação continua na fila (e
  // aparece em "Alterações não enviadas" se o servidor recusar depois).
  const espera = await comPrazo(sincronizar({ puxar: false }).catch(() => ({ online: false })), ESPERA_SERVIDOR_MS);
  if (espera.tempo) return gravada;
  const recusa = await armazem.recusaDoRegistro(tabela, linha.id);
  if (recusa) {
    // Recusou na hora: desfaz, para ficar como antes, e mostra o motivo no
    // formulário. Se o registro já tinha alterações esperando envio (feitas
    // sem sinal), elas ficam em "Alterações não enviadas" para corrigir.
    if (!tinhaPendencia) await armazem.desfazer(tabela, linha.id, anterior);
    await atualizarResumo();
    // `regra`: o código da regra do banco (HODOMETRO_SALTO…), para a tela agir.
    throw Object.assign(new Error(`RECUSADO: ${recusa.erro?.mensagem ?? 'o servidor não aceitou.'}`), { regra: recusa.erro?.regra ?? null });
  }
  // Traz por trás o que o servidor mudou em outras tabelas por causa disto.
  sincronizarLogo({ puxar: true, atraso: 1500 });
  return (await armazem.linhaAtual(tabela, linha.id)) ?? gravada;
}

async function criar(tabela, dados, opcoes = {}) {
  const momento = agora();
  const linha = comContasDa(tabela, {
    ...PADROES[tabela],
    ...dados,
    id: novoId(),
    empresa_id: empresaId(),
    created_at: momento,
    updated_at: momento,
    deleted_at: null,
  });
  return salvar(tabela, linha, { tipo: 'criar', empresaId: linha.empresa_id }, null, opcoes);
}

// Só os campos que mudaram vão para a fila: o formulário manda todos, e os
// que ficaram iguais não podem desfazer o que outro aparelho mudou neles.
// `base` é o registro como estava quando a tela abriu (o que a pessoa viu);
// sem ela, compara com o que está no aparelho agora.
async function atualizar(tabela, id, dados, base = null, opcoes = {}) {
  const atual = await armazem.uma(tabela, id);
  if (!atual || atual.empresa_id !== empresaId()) {
    throw Object.assign(new Error('O registro não existe mais. Atualize a tela.'), { code: 'PGRST116' });
  }
  const mudados = armazem.camposMudados(base ?? atual, dados);
  // Nada mudou: não há o que enviar (a não ser que o servidor tenha recusado
  // o registro antes; aí salvar de novo é tentar de novo).
  if (Object.keys(mudados).length === 0 && !(await armazem.recusaDoRegistro(tabela, id))) return atual;
  const linha = comContasDa(tabela, { ...atual, ...mudados, updated_at: agora() });
  return salvar(tabela, linha, { tipo: 'alterar', dados: mudados, empresaId: atual.empresa_id }, atual, opcoes);
}

function excluir(tabela, id, opcoes = {}) {
  return atualizar(tabela, id, { deleted_at: agora() }, null, opcoes);
}

// ---------------------------------------------------------------------
// Empresa (conta)
// ---------------------------------------------------------------------
export function criarEmpresa(d) {
  return executar(
    supabase.rpc('criar_empresa', {
      p_tipo_pessoa: d.tipo_pessoa,
      p_documento: d.documento,
      p_nome: d.nome,
      p_celular: d.celular,
      p_email: d.email || null,
      p_nome_fantasia: d.nome_fantasia || null,
      p_cidade: d.cidade || null,
      p_uf: d.uf || null,
    }),
  );
}

// ---------------------------------------------------------------------
// Usuários, convites e termos (E9): só com internet (passam por funções do
// servidor, que conferem quem é titular e o e-mail de quem foi convidado).
// ---------------------------------------------------------------------
// Grava no servidor que esta pessoa aceitou os Termos de uso e a Política de
// privacidade da versão atual (ao criar a conta e ao aceitar um convite).
export function aceitarTermos() {
  return executar(supabase.rpc('aceitar_termos', { p_versao: VERSAO_TERMOS }));
}

const rpcDaConta = async (acao, funcao, parametros) => {
  await exigirConexao(acao);
  return executar(supabase.rpc(funcao, parametros));
};

export const usuarios = {
  // Quem usa a conta (para o titular, também quem já usou) e os convites em
  // aberto (só o titular vê).
  listar: async () => {
    await exigirConexao('ver quem usa a conta');
    const [membros, convites] = await Promise.all([
      executar(supabase.rpc('membros_da_empresa', { p_empresa: empresaId() })),
      executar(supabase.from('convites').select('*').eq('empresa_id', empresaId())
        .is('aceito_em', null).is('recusado_em', null).is('cancelado_em', null)
        .order('enviado_em', { ascending: false })),
    ]);
    return { membros: membros ?? [], convites: convites ?? [] };
  },
  convidar: (email, papel) => rpcDaConta('convidar', 'convidar', { p_empresa: empresaId(), p_email: email, p_papel: papel }),
  cancelarConvite: (id) => rpcDaConta('cancelar o convite', 'cancelar_convite', { p_convite: id }),
  trocarPapel: (usuarioId, papel) =>
    rpcDaConta('trocar o papel', 'alterar_papel', { p_empresa: empresaId(), p_usuario: usuarioId, p_papel: papel }),
  tirarAcesso: (usuarioId) => rpcDaConta('tirar o acesso', 'tirar_acesso', { p_empresa: empresaId(), p_usuario: usuarioId }),
  sairDaEmpresa: () => rpcDaConta('sair da empresa', 'sair_da_empresa', { p_empresa: empresaId() }),
};

// Convites para o e-mail de quem está usando o app.
export const convites = {
  paraMim: () => executar(supabase.rpc('convites_para_mim')),
  aceitar: async (id) => {
    await exigirConexao('aceitar o convite');
    await aceitarTermos();
    return executar(supabase.rpc('aceitar_convite', { p_convite: id }));
  },
  recusar: (id) => rpcDaConta('recusar o convite', 'recusar_convite', { p_convite: id }),
};

// ---------------------------------------------------------------------
// App do motorista e do agenciador (0.21.0): o link pessoal, mandado pelo
// WhatsApp (só com internet; passam por funções do servidor). O código vai
// no link; o servidor guarda só o resumo dele (sha256).
// ---------------------------------------------------------------------
export const appDoMotorista = {
  // O link não vence: vale uma vez (o novo cancela o anterior).
  criarLink: async (colaboradorId) => {
    await exigirConexao('mandar o link do app');
    const codigo = novoCodigo();
    await executar(supabase.rpc('criar_link_do_app', {
      p_colaborador: colaboradorId, p_token_hash: await sha256Hex(codigo),
    }));
    return { codigo };
  },
  // { usando_desde, link_mandado_em } (null sem internet)
  situacao: async (colaboradorId) => {
    try {
      const linhas = await executar(supabase.rpc('acesso_do_motorista', { p_colaborador: colaboradorId }));
      return linhas?.[0] ?? null;
    } catch (erro) {
      if (ehErroDeRede(erro)) return null;
      throw erro;
    }
  },
  tirar: async (colaboradorId) => {
    await exigirConexao('tirar o acesso do app');
    return executar(supabase.rpc('tirar_acesso_do_motorista', { p_colaborador: colaboradorId }));
  },
};

// ---------------------------------------------------------------------
// Raio-X fora do escritório (0.20.0): os números e as preferências do
// Raio-X da empresa ficam no servidor (raiox_custos). O escritório calcula
// e grava; o app do motorista lê.
// ---------------------------------------------------------------------
const CHAVE_NUMEROS_ENVIADOS = 'hsafrota-raiox-numeros';
const UMA_HORA_MS = 60 * 60 * 1000;

export const raioxDaEmpresa = {
  // { reserva_km, sobra_pct, numeros, calculado_em, frete_regiao } ou null (nada ainda).
  ler: async () => {
    await exigirConexao('fazer o Raio-X');
    const linhas = await executar(supabase.from('raiox_custos').select('reserva_km, sobra_pct, numeros, calculado_em, frete_regiao').eq('empresa_id', empresaId()));
    return linhas?.[0] ?? null;
  },
  // Para o escritório: lê se der, sem travar a tela (sem internet, null).
  lerSeDer: async () => {
    if (!estado.online) return null;
    try {
      return await raioxDaEmpresa.ler();
    } catch (erro) {
      console.warn(`Raio-X: números do servidor: ${erro?.message ?? erro}`);
      return null;
    }
  },
  // A reserva por km e a sobra da empresa (sem internet, fica só no aparelho).
  guardarPreferencias: async (reserva, sobra) => {
    if (!estado.online) return;
    try {
      await executar(supabase.rpc('raiox_guardar_preferencias', { p_empresa: empresaId(), p_reserva: reserva, p_sobra: sobra }));
    } catch (erro) {
      console.warn(`Raio-X: preferências: ${erro?.message ?? erro}`);
    }
  },
  // O frete de retorno por região (Planejar o trajeto, 0.22.1): { N: 70, NE: 70, CO: null, … }.
  guardarFreteRegiao: async (frete) => {
    if (!estado.online) return;
    try {
      await executar(supabase.rpc('raiox_guardar_frete_regiao', { p_empresa: empresaId(), p_frete: frete }));
    } catch (erro) {
      console.warn(`Planejar o trajeto: frete por região: ${erro?.message ?? erro}`);
    }
  },
  // Os números calculados neste aparelho, no máximo uma vez por hora.
  guardarNumeros: async (numeros) => {
    if (!estado.online) return;
    const chave = `${CHAVE_NUMEROS_ENVIADOS}:${empresaId()}`;
    try {
      if (Date.now() - Number(localStorage.getItem(chave) ?? 0) < UMA_HORA_MS) return;
    } catch {
      // Sem armazenamento: manda sempre.
    }
    try {
      await executar(supabase.rpc('raiox_guardar_numeros', { p_empresa: empresaId(), p_numeros: numeros }));
      try {
        localStorage.setItem(chave, String(Date.now()));
      } catch {
        // Sem armazenamento: só não lembra.
      }
    } catch (erro) {
      console.warn(`Raio-X: números: ${erro?.message ?? erro}`);
    }
  },
  // O que a empresa já recebeu na rota (o motorista não tem todas as cargas no aparelho).
  historico: async ({ origemCidade, origemUf, destinoCidade, destinoUf }) => {
    const linhas = await executar(supabase.rpc('historico_da_rota', {
      p_empresa: empresaId(), p_origem_cidade: origemCidade, p_origem_uf: origemUf, p_destino_cidade: destinoCidade, p_destino_uf: destinoUf,
    }));
    const x = linhas?.[0];
    if (!x) return null;
    return {
      cargas: x.cargas,
      media: Number(x.media),
      menor: Number(x.menor),
      maior: Number(x.maior),
      ultima: { data: x.ultima_data, valor: Number(x.ultima_valor) },
    };
  },
};

// Planejar o trajeto (0.22.0): o pedaço do mapa do CIOT entre onde o caminhão
// está e o destino (as contas ficam em lib/trajeto.js). Precisa de internet.
export async function planejarTrajeto({ origemCidade, origemUf, destinoCidade = null, destinoUf = null, raioKm = 100, grupos = null }) {
  await exigirConexao('planejar o trajeto');
  return executar(supabase.rpc('planejar_trajeto', {
    p_empresa: empresaId(),
    p_origem_cidade: origemCidade,
    p_origem_uf: origemUf,
    p_destino_cidade: destinoCidade,
    p_destino_uf: destinoUf,
    p_raio_km: raioKm,
    p_grupos: grupos,
  }));
}

// O celular do motorista ou do agenciador entra sem e-mail (usuário
// anônimo) e troca o código do link pelo acesso. Devolve o usuário e o papel
// ('motorista' ou 'agenciador').
export async function entrarPeloLink(codigo) {
  const { data: atual } = await supabase.auth.getSession();
  let usuario = atual?.session?.user ?? null;
  if (!usuario) {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    usuario = data.user;
  }
  const papel = await executar(supabase.rpc('entrar_pelo_link', { p_token: codigo }));
  await aceitarTermos();
  try {
    localStorage.setItem(CHAVE_LINK_USADO, JSON.stringify({ usuario: usuario.id, resumo: await sha256Hex(codigo) }));
  } catch {
    /* sem o atalho: abrir o link de novo pergunta ao servidor */
  }
  return { usuario, papel };
}

// Os caminhões vazios da empresa, onde ficaram e desde quando (o agenciador
// não tem as cargas dos outros no celular; precisa de internet).
export async function caminhoesVazios() {
  await exigirConexao('ver os caminhões vazios');
  const linhas = (await executar(supabase.rpc('caminhoes_vazios', { p_empresa: empresaId() }))) ?? [];
  return linhas.map((x) => ({
    veiculo: { id: x.veiculo_id, placa: x.placa, apelido: x.apelido },
    estado: 'vazio',
    viagem: x.em_viagem ? {} : null,
    lugar: x.cidade || x.uf ? { cidade: x.cidade ?? null, uf: x.uf ?? null } : null,
    desde: x.entregue_em ?? x.data_descarga ?? null,
  }));
}

// O link que este celular já usou: abrir o mesmo link de novo entra direto,
// sem internet também (o resumo fica guardado, não o código).
const CHAVE_LINK_USADO = 'maxcargo-link-motorista';

export async function linkJaUsadoAqui(codigo) {
  let guardado = null;
  try {
    guardado = JSON.parse(localStorage.getItem(CHAVE_LINK_USADO) ?? 'null');
  } catch {
    return false;
  }
  return Boolean(guardado?.resumo) && guardado.usuario === sessao.usuario?.id && guardado.resumo === (await sha256Hex(codigo));
}

// ---------------------------------------------------------------------
// Oficinas de confiança (SOS mecânica): gravadas no aparelho, pela fila.
// ---------------------------------------------------------------------
export const oficinas = {
  listar: () => listar('oficinas', porTexto('nome')),
  obter: (id) => obter('oficinas', id),
  criar: (d) => criar('oficinas', d),
  atualizar: (id, d, base) => atualizar('oficinas', id, d, base),
  excluir: (id) => excluir('oficinas', id),
};

// Dados cadastrais da empresa: gravados no aparelho e enviados pela fila.
export async function atualizarEmpresa(dados, base = null) {
  const anterior = sessao.empresa;
  const mudados = armazem.camposMudados(base ?? anterior, dados);
  if (Object.keys(mudados).length === 0 && !(await armazem.recusaDoRegistro('empresas', anterior.id))) return anterior;
  const linha = { ...anterior, ...mudados, updated_at: agora() };
  const gravada = (await salvar('empresas', linha, { tipo: 'alterar', dados: mudados, empresaId: linha.id }, anterior)) ?? linha;
  atualizarEmpresaNaSessao(gravada);
  return gravada;
}

// ---------------------------------------------------------------------
// Veículos e licenças
// ---------------------------------------------------------------------
const ordemVeiculos = (a, b) => (a.tipo === b.tipo ? porTexto('placa')(a, b) : a.tipo < b.tipo ? 1 : -1);

// Manutenção preventiva: o plano de cada caminhão (itens da lista pronta ou
// outros). A última vez feita pelas despesas, o banco anota no item.
export const manutencao = {
  listar: () => listar('manutencao_itens', porTexto('nome')),
  doVeiculo: async (veiculoId) => (await manutencao.listar()).filter((i) => i.veiculo_id === veiculoId),
  obter: (id) => obter('manutencao_itens', id),
  criar: (d, opcoes) => criar('manutencao_itens', d, opcoes),
  atualizar: (id, d, base) => atualizar('manutencao_itens', id, d, base),
  excluir: (id) => excluir('manutencao_itens', id),
  // Monta o plano com vários itens de uma vez (sem esperar o servidor a cada um).
  montar: async (lista) => {
    const criados = [];
    for (const d of lista) criados.push(await criar('manutencao_itens', d, { esperarServidor: false }));
    return criados;
  },
};

export const veiculos = {
  listar: () => listar('veiculos', ordemVeiculos),
  obter: (id) => obter('veiculos', id),
  criar: (d) => criar('veiculos', d),
  atualizar: (id, d, base) => atualizar('veiculos', id, d, base),
  excluir: (id) => excluir('veiculos', id),
};

// Placa: só com internet. Antes, o caminhão (que pode ter sido cadastrado sem
// sinal) precisa ter chegado ao servidor; depois, baixa as licenças de novo.
async function acaoDePlaca(veiculoIds, acao, chamada) {
  await exigirConexao(acao);
  const pendentes = await armazem.situacoes('veiculos');
  if (veiculoIds.some((id) => pendentes.has(id))) {
    throw new Error(
      'CAMINHAO_NAO_ENVIADO: este caminhão ainda não chegou ao servidor. Confira em "Alterações não enviadas" e tente de novo.',
    );
  }
  const resultado = await executar(chamada());
  // A placa já mudou no servidor; se a baixa falhar agora (sinal caiu), ela
  // vem na próxima.
  await puxarAgora(empresaId(), ['licencas', 'veiculos']).catch(() => sincronizarLogo({ puxar: true, atraso: 5000 }));
  return resultado;
}

export const licencas = {
  listar: () => listar('licencas', decrescente('inicio')),
  doVeiculo: async (veiculoId) => (await listar('licencas', decrescente('inicio'))).filter((l) => l.veiculo_id === veiculoId),
  incluir: (veiculoId, inicio = null) =>
    acaoDePlaca([veiculoId], 'ativar a placa', () =>
      supabase.rpc('incluir_placa', { p_veiculo: veiculoId, ...(inicio ? { p_inicio: inicio } : {}) })),
  retirar: (veiculoId) =>
    acaoDePlaca([veiculoId], 'retirar a placa', () => supabase.rpc('retirar_placa', { p_veiculo: veiculoId })),
  trocar: (sai, entra) =>
    acaoDePlaca([sai, entra], 'trocar a placa', () => supabase.rpc('trocar_placa', { p_sai: sai, p_entra: entra })),
};

// ---------------------------------------------------------------------
// Colaboradores e regras de remuneração
// ---------------------------------------------------------------------
export const colaboradores = {
  listar: () => listar('colaboradores', porTexto('nome')),
  obter: (id) => obter('colaboradores', id),
  criar: (d) => criar('colaboradores', d),
  atualizar: (id, d, base) => atualizar('colaboradores', id, d, base),
  excluir: (id) => excluir('colaboradores', id),
};

export const regras = {
  listar: () => listar('regras_remuneracao', decrescente('vigente_de')),
  doColaborador: async (colaboradorId) =>
    (await listar('regras_remuneracao', decrescente('vigente_de'))).filter((r) => r.colaborador_id === colaboradorId),
  obter: (id) => obter('regras_remuneracao', id),
  criar: (d) => criar('regras_remuneracao', d),
  atualizar: (id, d, base) => atualizar('regras_remuneracao', id, d, base),
  excluir: (id) => excluir('regras_remuneracao', id),
};

// ---------------------------------------------------------------------
// Clientes
// ---------------------------------------------------------------------
export const clientes = {
  listar: () => listar('clientes', porTexto('nome')),
  obter: (id) => obter('clientes', id),
  criar: (d) => criar('clientes', d),
  atualizar: (id, d, base) => atualizar('clientes', id, d, base),
  excluir: (id) => excluir('clientes', id),
};

// ---------------------------------------------------------------------
// Viagens, cargas e vales
// ---------------------------------------------------------------------
export const viagens = {
  listar: () => listar('viagens', recentes('data_saida')),
  obter: (id) => obter('viagens', id),
  criar: (d) => criar('viagens', d),
  atualizar: (id, d, base) => atualizar('viagens', id, d, base),
  // A viagem vai para a lixeira com as cargas e os vales dela (primeiro eles:
  // o banco não deixa excluir viagem com lançamentos).
  excluir: async (id) => {
    for (const f of await fretes.daViagem(id)) await excluir('fretes', f.id);
    for (const l of (await listar('lancamentos')).filter((x) => x.viagem_id === id)) {
      if (l.tipo === 'despesa') await despesas.excluir(l.id);
      else await excluir('lancamentos', l.id);
    }
    return excluir('viagens', id);
  },
};

export const fretes = {
  listar: () => listar('fretes', crescente('data_carregamento')),
  daViagem: async (viagemId) => (await listar('fretes', crescente('data_carregamento'))).filter((f) => f.viagem_id === viagemId),
  obter: (id) => obter('fretes', id),
  criar: (d) => criar('fretes', d),
  atualizar: (id, d, base) => atualizar('fretes', id, d, base),
  excluir: (id) => excluir('fretes', id),
  // O agenciamento desta carga já entrou num acerto fechado (do agenciador)?
  agenciamentoAcertado: async (id) => (await listar('lancamentos'))
    .some((l) => l.frete_id === id && l.tipo === 'credito' && l.subtipo === 'agenciamento' && l.acerto_id),
};

// As paradas de cada carga antes do destino final (0.19.0).
const porOrdem = (a, b) => a.ordem - b.ordem || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''));
export const entregas = {
  listar: () => listar('entregas', porOrdem),
  daCarga: async (freteId) => (await listar('entregas', porOrdem)).filter((e) => e.frete_id === freteId),
  criar: (d, opcoes) => criar('entregas', d, opcoes),
  atualizar: (id, d, base, opcoes) => atualizar('entregas', id, d, base, opcoes),
  excluir: (id, opcoes) => excluir('entregas', id, opcoes),
  // Grava as paradas como ficaram na tela: as novas, as mudadas e as tiradas.
  //   linhas: [{ id?, cidade, uf, data_entrega, observacao }] na ordem
  salvarDaCarga: async (freteId, linhas, antigas = []) => {
    const ficam = new Set(linhas.filter((l) => l.id).map((l) => l.id));
    for (const a of antigas) if (!ficam.has(a.id)) await excluir('entregas', a.id, { esperarServidor: false });
    for (const [i, l] of linhas.entries()) {
      const dados = { ordem: i + 1, cidade: l.cidade, uf: l.uf, data_entrega: l.data_entrega || null, observacao: l.observacao || null };
      if (l.id) await atualizar('entregas', l.id, dados, antigas.find((a) => a.id === l.id) ?? null, { esperarServidor: false });
      else await criar('entregas', { ...dados, frete_id: freteId }, { esperarServidor: false });
    }
    sincronizarLogo();
  },
};

// Modo viagem (0.23.0): o que acontece na viagem (iniciar, o previsto, chegou
// e saiu para carregar ou descarregar, paradas, SOS, encerrar). Sem sinal,
// fica na fila e sobe depois.
const porMomento = (a, b) => String(a.momento).localeCompare(String(b.momento))
  || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''));
export const eventosDaViagem = {
  listar: () => listar('viagem_eventos', porMomento),
  daViagem: async (viagemId) => (await listar('viagem_eventos', porMomento)).filter((e) => e.viagem_id === viagemId),
  criar: (d, opcoes) => criar('viagem_eventos', d, opcoes),
  atualizar: (id, d, base, opcoes) => atualizar('viagem_eventos', id, d, base, opcoes),
  excluir: (id, opcoes) => excluir('viagem_eventos', id, opcoes),
};

// Todos os lançamentos de uma viagem (despesas, vales e créditos do motorista).
export async function lancamentosDaViagem(viagemId) {
  return (await listar('lancamentos')).filter((l) => l.viagem_id === viagemId);
}

// As coordenadas das cidades da viagem e as cidades-polo (só com internet).
// { cidades: [{ cidade, uf, lat, lon } | null], polos: [{ nome, uf, lat, lon }] }
export async function lugaresDaViagem(lugares) {
  await exigirConexao('montar a rota da viagem');
  return executar(supabase.rpc('lugares_da_viagem', {
    p_cidades: lugares.map((l) => l.cidade ?? ''), p_ufs: lugares.map((l) => l.uf ?? ''),
  }));
}

// "Carga entregue" (no destino final): a data e a hora, e as paradas que
// faltavam ficam entregues no mesmo dia.
export async function cargaEntregue(frete, { hoje, agora = new Date() }) {
  for (const p of (await entregas.daCarga(frete.id)).filter((x) => !x.data_entrega)) {
    await atualizar('entregas', p.id, { data_entrega: hoje }, p, { esperarServidor: false });
  }
  return atualizar('fretes', frete.id, { data_descarga: frete.data_descarga ?? hoje, entregue_em: agora.toISOString() }, frete);
}

// Uma parada entregue (a carga continua no caminhão).
export function paradaEntregue(parada, { hoje }) {
  return atualizar('entregas', parada.id, { data_entrega: hoje }, parada);
}

const soVales = (lista) => lista.filter((l) => l.tipo === 'vale');

export const vales = {
  listar: async () => soVales(await listar('lancamentos', recentes('data'))),
  daViagem: async (viagemId) => soVales(await listar('lancamentos', recentes('data'))).filter((l) => l.viagem_id === viagemId),
  doColaborador: async (colaboradorId) => soVales(await listar('lancamentos', recentes('data'))).filter((l) => l.colaborador_id === colaboradorId),
  obter: async (id) => {
    const l = await obter('lancamentos', id);
    return l?.tipo === 'vale' ? l : null;
  },
  criar: (d) => criar('lancamentos', { ...d, tipo: 'vale' }),
  atualizar: (id, d, base, opcoes) => atualizar('lancamentos', id, d, base, opcoes),
  excluir: (id) => excluir('lancamentos', id),
};

// ---------------------------------------------------------------------
// Despesas, parcelas, fotos e contas fixas (E5)
// ---------------------------------------------------------------------
const soDespesas = (lista) => lista.filter((l) => l.tipo === 'despesa');
const porVencimento = crescente('vencimento');

const ordemCategorias = (a, b) => (a.ordem ?? 100) - (b.ordem ?? 100) || porTexto('nome')(a, b);

export const categorias = {
  // As que podem ser escolhidas.
  listar: async () => (await armazem.categorias(empresaId())).filter((c) => !c.deleted_at && c.ativo !== false).sort(ordemCategorias),
  // Todas, para mostrar o nome das despesas antigas.
  todas: async () => (await armazem.categorias(empresaId())).sort(ordemCategorias),
};

export const parcelas = {
  listar: () => listar('parcelas', porVencimento),
  doLancamento: async (lancamentoId) => (await listar('parcelas', crescente('numero'))).filter((p) => p.lancamento_id === lancamentoId),
  pagar: (id, { pago_em, forma }) => atualizar('parcelas', id, { pago_em, forma }),
  reabrir: (id) => atualizar('parcelas', id, { pago_em: null }),
};

export const anexos = {
  todos: () => listar('anexos', crescente('created_at')),
  doLancamento: async (lancamentoId) => (await listar('anexos', crescente('created_at'))).filter((a) => a.lancamento_id === lancamentoId),
  doFrete: async (freteId) => (await listar('anexos', crescente('created_at'))).filter((a) => a.frete_id === freteId),
  // Guarda a foto no aparelho e põe na fila; ela sobe (antes do anexo) quando
  // houver sinal. Caminho no bucket: {empresa}/{ano}/{mês}/{anexo}.{ext}
  // documento: a foto de qual documento da carga (modo viagem: cte, mdfe, nfe…).
  adicionar: async ({ lancamento_id = null, frete_id = null, dia, documento = null }, arquivo) => {
    const id = novoId();
    const [ano, mes] = dia.split('-');
    const momento = agora();
    const linha = {
      ...PADROES.anexos,
      id,
      empresa_id: empresaId(),
      lancamento_id,
      frete_id,
      ...(documento ? { documento } : {}),
      tipo: arquivo.tipo,
      storage_path: `${empresaId()}/${ano}/${mes}/${id}.${extensao(arquivo.mime)}`,
      mime: arquivo.mime,
      bytes: arquivo.bytes,
      sha256: arquivo.sha256,
      created_at: momento,
      updated_at: momento,
      deleted_at: null,
    };
    await armazem.gravar('anexos', linha, {
      tipo: 'criar',
      empresaId: linha.empresa_id,
      arquivo: { caminho: linha.storage_path, mime: arquivo.mime, bytes: arquivo.bytes, sha256: arquivo.sha256, blob: arquivo.blob },
    });
    sincronizarLogo();
    return linha;
  },
  // O arquivo fica no servidor (é prova); o anexo vai para a lixeira.
  remover: (id) => excluir('anexos', id),
  arquivoLocal: (id) => armazem.arquivo(id),
  // Endereço para mostrar a foto: a guardada no aparelho ou, com sinal, um
  // link temporário do servidor. null quando não dá (sem sinal e sem cópia).
  endereco: async (anexo) => {
    if (enderecos.has(anexo.id)) return enderecos.get(anexo.id);
    const local = await armazem.arquivo(anexo.id);
    if (local?.blob) {
      const url = URL.createObjectURL(local.blob);
      enderecos.set(anexo.id, url);
      return url;
    }
    if (!estado.online) return null;
    const r = await comPrazo(supabase.storage.from('comprovantes').createSignedUrl(anexo.storage_path, 3600), 10000);
    return r.valor?.data?.signedUrl ?? null;
  },
  // O arquivo em si (para o .zip do contador): a cópia do aparelho ou, com
  // sinal, o do servidor. null quando não dá.
  baixar: async (anexo) => {
    const local = await armazem.arquivo(anexo.id);
    if (local?.blob) return local.blob;
    if (!estado.online) return null;
    const r = await comPrazo(supabase.storage.from('comprovantes').download(anexo.storage_path), 30000);
    return r.valor?.data ?? null;
  },
};
const enderecos = new Map();

// Despesa (e abastecimento): o lançamento, as parcelas se for a prazo e as
// fotos. Com sinal, espera o servidor aceitar a despesa (hodômetro fora da
// sequência, placa sem licença… aparecem no formulário); parcelas e fotos
// vão logo atrás, sem esperar.
export const despesas = {
  listar: async () => soDespesas(await listar('lancamentos', recentes('data'))),
  daViagem: async (viagemId) => (await despesas.listar()).filter((l) => l.viagem_id === viagemId),
  doVeiculo: async (veiculoId) => (await despesas.listar()).filter((l) => l.veiculo_id === veiculoId),
  // Leituras de hodômetro (de qualquer lançamento) e abastecimentos (com ou
  // sem hodômetro: os litros do sem km entram no consumo), de todos os veículos.
  leituras: async () => (await listar('lancamentos'))
    .filter((l) => l.veiculo_id && ((l.hodometro !== null && l.hodometro !== undefined) || Number(l.litros) > 0)),
  obter: async (id) => {
    const l = await obter('lancamentos', id);
    return l?.tipo === 'despesa' ? l : null;
  },
  criar: async (dados, { parcelas: lista = [], fotos = [] } = {}) => {
    const despesa = await criar('lancamentos', { ...dados, tipo: 'despesa' });
    for (const p of lista) await criar('parcelas', { ...p, lancamento_id: despesa.id }, { esperarServidor: false });
    for (const f of fotos) await anexos.adicionar({ lancamento_id: despesa.id, dia: despesa.data }, f);
    return despesa;
  },
  // `parcelas`: a lista nova, quando o valor, a condição ou as parcelas
  // mudaram (as antigas vão para a lixeira). A despesa vai primeiro: se o
  // servidor recusar, as parcelas ficam como estavam.
  atualizar: async (id, d, base, { parcelas: novas = null } = {}) => {
    const despesa = await atualizar('lancamentos', id, d, base);
    if (novas) {
      for (const p of await parcelas.doLancamento(id)) await excluir('parcelas', p.id, { esperarServidor: false });
      for (const p of novas) await criar('parcelas', { ...p, lancamento_id: id }, { esperarServidor: false });
    }
    return despesa;
  },
  // Vai para a lixeira com as parcelas e os anexos dela.
  excluir: async (id) => {
    for (const p of await parcelas.doLancamento(id)) await excluir('parcelas', p.id, { esperarServidor: false });
    for (const a of await anexos.doLancamento(id)) await excluir('anexos', a.id, { esperarServidor: false });
    return excluir('lancamentos', id);
  },
  // As despesas geradas por uma conta fixa.
  daContaFixa: async (contaId) => (await despesas.listar()).filter((l) => l.recorrencia_id === contaId),
};

export const contasFixas = {
  listar: () => listar('contas_fixas', porTexto('descricao')),
  obter: (id) => obter('contas_fixas', id),
  criar: async (d) => {
    const conta = await criar('contas_fixas', d);
    gerarContasFixasLogo(empresaId());
    return conta;
  },
  atualizar: async (id, d, base) => {
    const conta = await atualizar('contas_fixas', id, d, base);
    gerarContasFixasLogo(empresaId());
    return conta;
  },
  excluir: (id) => excluir('contas_fixas', id),
};

// O que falta pagar: parcelas em aberto e o agenciamento das transportadoras.
export async function aPagar() {
  const [parcelas, lancamentos, fretes, clientes, acertos, colaboradores] = await Promise.all([
    listar('parcelas'), listar('lancamentos'), listar('fretes'), listar('clientes'), listar('acertos'), listar('colaboradores'),
  ]);
  return itensAPagar({ parcelas, lancamentos, fretes, clientes, acertos, colaboradores });
}

// O que os contratantes ainda devem e os comprovantes (tela "A receber").
export async function cargasEClientes() {
  const [cargas, listaClientes, listaViagens, frota] = await Promise.all([
    listar('fretes', crescente('data_carregamento')), listar('clientes'), listar('viagens'), listar('veiculos'),
  ]);
  return { fretes: cargas, clientes: listaClientes, viagens: listaViagens, veiculos: frota };
}

// "Recebi": a data do adiantamento ou do saldo (vale também em carga acertada).
export function receber(freteId, tipo, dia) {
  return atualizar('fretes', freteId, tipo === 'adiantamento' ? { adto_recebido_em: dia } : { saldo_recebido_em: dia });
}

export function postarComprovante(freteId, { postado_em, rastreio = null }) {
  return atualizar('fretes', freteId, { comprovante_status: 'postado', comprovante_postado_em: postado_em, comprovante_rastreio: rastreio });
}

export function comprovanteEntregue(freteId) {
  return atualizar('fretes', freteId, { comprovante_status: 'entregue' });
}

export function pagarAgenciamento(freteId, dia) {
  return atualizar('fretes', freteId, { agenciamento_pago_em: dia });
}

// O que foi pago de `desde` em diante (para desfazer um pagamento marcado
// por engano).
export async function pagosDesde(desde) {
  const [todasParcelas, lancs, cargas, listaClientes] = await Promise.all([
    listar('parcelas'), listar('lancamentos'), listar('fretes'), listar('clientes'),
  ]);
  const porId = new Map(lancs.map((l) => [l.id, l]));
  const itens = todasParcelas
    .filter((p) => p.pago_em && p.pago_em >= desde && porId.has(p.lancamento_id))
    .map((p) => ({ tipo: 'parcela', id: p.id, pago_em: p.pago_em, vencimento: p.vencimento, valor: Number(p.valor), parcela: p, despesa: porId.get(p.lancamento_id) }));
  for (const f of cargas) {
    if (f.agenciado_por !== 'transportadora' || !f.agenciamento_pago_em || f.agenciamento_pago_em < desde) continue;
    itens.push({
      tipo: 'agenciamento', id: f.id, pago_em: f.agenciamento_pago_em, vencimento: f.data_carregamento, valor: Number(f.agenciamento_valor), frete: f,
      cliente: listaClientes.find((c) => c.id === f.cliente_id) ?? null,
    });
  }
  const [listaAcertos, pessoas] = await Promise.all([listar('acertos'), listar('colaboradores')]);
  for (const a of listaAcertos) {
    if (a.quitacao !== 'paga' || !a.quitado_em || a.quitado_em < desde) continue;
    itens.push({
      tipo: 'acerto', id: a.id, pago_em: a.quitado_em, vencimento: a.data, valor: -Number(a.saldo), acerto: a,
      colaborador: pessoas.find((p) => p.id === a.colaborador_id) ?? null,
    });
  }
  return itens.sort((a, b) => (a.pago_em < b.pago_em ? 1 : a.pago_em > b.pago_em ? -1 : 0));
}

export function desfazerAgenciamentoPago(freteId) {
  return atualizar('fretes', freteId, { agenciamento_pago_em: null });
}

// O jeito de pagar usado da última vez em cada categoria (só neste aparelho):
// a próxima despesa já vem assim.
export function ultimoJeito(categoriaId) {
  return armazem.lerLocal(`despesa:${empresaId()}:${categoriaId}`);
}

export function lembrarJeito(categoriaId, jeito) {
  return armazem.gravarLocal(`despesa:${empresaId()}:${categoriaId}`, jeito);
}

// ---------------------------------------------------------------------
// Acertos (E6). A prévia é feita no aparelho (funciona sem sinal); fechar,
// quitar e desfazer passam por funções do servidor (só com internet), que
// numeram, congelam os totais e travam o que entrou.
// ---------------------------------------------------------------------
const maisRecenteAntes = (a, b) => String(b.fechado_em ?? '').localeCompare(String(a.fechado_em ?? ''));

export const acertos = {
  // Os que valem (os desfeitos ficam de fora), do mais recente para o mais antigo.
  listar: () => listar('acertos', maisRecenteAntes),
  doColaborador: async (id) => (await listar('acertos', maisRecenteAntes)).filter((a) => a.colaborador_id === id),
  // Com os desfeitos, para o histórico.
  historico: async (id) => (await armazem.todas('acertos', empresaId()))
    .filter((a) => a.colaborador_id === id).sort(maisRecenteAntes),
  // Todos, com os desfeitos (para o relatório entre datas).
  todos: async () => (await armazem.todas('acertos', empresaId())).sort(maisRecenteAntes),
  obter: async (id) => {
    const a = await armazem.uma('acertos', id);
    return a && a.empresa_id === empresaId() ? a : null;
  },
  // O que entrou no acerto. A viagem que estava em andamento entra em parte
  // (`parcial`): as cargas carregadas até a data do acerto.
  itens: async (acertoId) => itensDoAcerto(acertoId, await tudoDosAcertos()),
  // Acertos (que valem) com parte de uma viagem que estava em andamento:
  // cargas, diárias ou o valor da viagem. `pagaAte`: último dia de diária pago.
  partesDaViagem: async (viagemId) => {
    const [fs, ls] = await Promise.all([listar('fretes'), listar('lancamentos')]);
    const creditos = ls.filter((l) => l.viagem_id === viagemId && l.tipo === 'credito' && l.acerto_id);
    const ids = new Set([...fs.filter((f) => f.viagem_id === viagemId && f.acerto_id).map((f) => f.acerto_id), ...creditos.map((l) => l.acerto_id)]);
    const lista = (await Promise.all([...ids].map((id) => acertos.obter(id)))).filter((a) => a && !a.deleted_at).sort(maisRecenteAntes);
    const pagaAte = creditos.filter((l) => l.subtipo === 'diaria').map((l) => l.data).sort().at(-1) ?? null;
    return { acertos: lista, pagaAte };
  },
  // Só a assinatura muda depois de fechado.
  atualizar: (id, d, base, opcoes) => atualizar('acertos', id, d, base, opcoes),
};

// Viagens, cargas e lançamentos (para montar o que entrou em cada acerto).
export async function tudoDosAcertos() {
  const [viagens, fretes, lancamentos] = await Promise.all([
    listar('viagens'), listar('fretes', crescente('data_carregamento')), listar('lancamentos', crescente('data')),
  ]);
  return { viagens, fretes, lancamentos };
}

// Tudo o que a prévia do acerto de um colaborador precisa.
export async function dadosDaPrevia(colaboradorId) {
  const [listaViagens, listaFretes, lancs, listaRegras, dele] = await Promise.all([
    listar('viagens'), listar('fretes'), listar('lancamentos'), listar('regras_remuneracao'), acertos.doColaborador(colaboradorId),
  ]);
  return { viagens: listaViagens, fretes: listaFretes, lancamentos: lancs, regras: listaRegras, ultimoAcerto: dele[0] ?? null };
}

// Crédito (bônus…) ou desconto (avaria, multa…) lançado à mão para o acerto.
export const ajustes = {
  criar: (d) => criar('lancamentos', d),
  excluir: (id) => excluir('lancamentos', id),
};

// Antes de fechar: manda o que está na fila e baixa o que mudou, para a
// prévia final ser feita com os dados do servidor.
export async function prepararFechamento() {
  await exigirConexao('fechar o acerto');
  await puxarAgora(empresaId(), ['regras_remuneracao', 'acertos', 'viagens', 'fretes', 'lancamentos']);
}

export async function fecharAcerto({ colaboradorId, data, fechamento, observacao = null }) {
  await exigirConexao('fechar o acerto');
  const situacoes = await Promise.all(['viagens', 'fretes', 'lancamentos'].map((t) => armazem.situacoes(t)));
  const ids = [
    ...fechamento.viagens, ...fechamento.fretes, ...fechamento.lancamentos, ...fechamento.creditos.map((c) => c.frete_id).filter(Boolean),
  ];
  if (ids.some((id) => situacoes.some((m) => m.has(id)))) {
    throw new Error('ACERTO_COM_PENDENCIA: há lançamentos deste acerto que ainda não chegaram ao servidor. Confira em "Alterações não enviadas" e tente de novo.');
  }
  let r;
  try {
    r = await executar(supabase.rpc('fechar_acerto', {
      p_colaborador: colaboradorId,
      p_data: data,
      p_viagens: fechamento.viagens,
      p_fretes: fechamento.fretes,
      p_lancamentos: fechamento.lancamentos,
      p_creditos: fechamento.creditos,
      p_saldo_esperado: fechamento.totais.saldo,
      p_observacao: observacao,
    }));
  } catch (erro) {
    if (!ehErroDeRede(erro)) throw erro;
    // A resposta não chegou: o servidor pode ter fechado assim mesmo. Se o
    // acerto está lá, segue com ele; senão, nada foi gravado.
    const fechado = await acertoQueFechou(colaboradorId, fechamento).catch(() => null);
    if (!fechado) {
      throw new Error('SEM_RESPOSTA: o servidor não respondeu e o acerto não foi fechado. Confira o sinal e tente de novo.');
    }
    return { id: fechado.id, numero: fechado.numero, ano: fechado.ano, saldo: Number(fechado.saldo) };
  }
  await puxarAgora(empresaId(), ['acertos', 'viagens', 'fretes', 'lancamentos']).catch(() => sincronizarLogo({ puxar: true, atraso: 3000 }));
  return r;
}

// Baixa de novo e vê se o que ia entrar no acerto já está num acerto dele.
async function acertoQueFechou(colaboradorId, fechamento) {
  await puxarAgora(empresaId(), ['acertos', 'viagens', 'fretes', 'lancamentos']);
  const [vs, fs, ls] = await Promise.all([listar('viagens'), listar('fretes'), listar('lancamentos')]);
  const ids = new Set([...fechamento.viagens, ...(fechamento.fretes ?? []), ...fechamento.lancamentos]);
  const marcado = [...vs, ...fs, ...ls].find((x) => ids.has(x.id) && x.acerto_id);
  if (marcado) {
    const a = await acertos.obter(marcado.acerto_id);
    if (a && !a.deleted_at && a.colaborador_id === colaboradorId) return a;
  }
  // Acerto só com créditos novos (agenciamentos, fixo): o crédito da carga.
  const freteIds = new Set(fechamento.creditos.map((c) => c.frete_id).filter(Boolean));
  const credito = ls.find((l) => l.tipo === 'credito' && l.acerto_id && l.colaborador_id === colaboradorId && freteIds.has(l.frete_id));
  return credito ? acertos.obter(credito.acerto_id) : null;
}

// como: 'paga' (a empresa pagou) · 'devolvida' (o colaborador devolveu) ·
// 'transportada' (o saldo vai para o próximo acerto)
export async function quitarAcerto(id, { como, data, forma = null }) {
  await exigirConexao('registrar a quitação do acerto');
  const r = await executar(supabase.rpc('quitar_acerto', { p_acerto: id, p_como: como, p_data: data, p_forma: forma }));
  await puxarAgora(empresaId(), ['acertos', 'lancamentos']).catch(() => sincronizarLogo({ puxar: true, atraso: 3000 }));
  return r;
}

export async function desfazerAcerto(id, motivo) {
  await exigirConexao('desfazer o acerto');
  const r = await executar(supabase.rpc('desfazer_acerto', { p_acerto: id, p_motivo: motivo }));
  await puxarAgora(empresaId(), ['acertos', 'viagens', 'fretes', 'lancamentos']).catch(() => sincronizarLogo({ puxar: true, atraso: 3000 }));
  return r;
}

// ---------------------------------------------------------------------
// Relatórios (E8): tudo o que eles precisam, do aparelho (sem sinal também).
// ---------------------------------------------------------------------
export async function dadosDosRelatorios() {
  const [frota, lics, vs, fs, ls, cats, rs, pessoas, clis, parcs, anxs, evs] = await Promise.all([
    armazem.todas('veiculos', empresaId()), listar('licencas'), listar('viagens'), listar('fretes'), listar('lancamentos'),
    armazem.categorias(empresaId()), listar('regras_remuneracao'), listar('colaboradores', porTexto('nome')), listar('clientes'),
    listar('parcelas'), listar('anexos'), listar('viagem_eventos'),
  ]);
  return {
    veiculos: frota, licencas: lics, viagens: vs, fretes: fs, lancamentos: ls, categorias: cats, regras: rs,
    colaboradores: pessoas, clientes: clis, parcelas: parcs, anexos: anxs, eventos: evs,
  };
}

// Cópia dos dados da empresa (LGPD): todas as tabelas que estão no aparelho,
// com o que está na lixeira (marcado em deleted_at), e os dados da empresa.
export async function dadosDaCopia() {
  const tabelas = {};
  for (const tabela of TABELAS) {
    tabelas[tabela] = tabela === 'categorias' ? await armazem.categorias(empresaId()) : await armazem.todas(tabela, empresaId());
  }
  return { empresa: sessao.empresa, tabelas };
}

// Código do link de assinatura de um vale (ou acerto), guardado só neste
// aparelho (o banco guarda o sha256). Serve para mandar o mesmo link de novo.
export async function codigoDoLink(valeId) {
  return armazem.lerLocal(`link-assinatura:${valeId}`);
}

export async function guardarCodigoDoLink(valeId, registro) {
  return armazem.gravarLocal(`link-assinatura:${valeId}`, registro);
}

// ---------------------------------------------------------------------
// Contagens para a tela inicial
// ---------------------------------------------------------------------
export async function contar(tabela, filtro = () => true) {
  return (await listar(tabela)).filter(filtro).length;
}

// Situação de envio dos registros de uma tabela: Map(id → 'pendente' | 'recusada').
export function situacoesDeEnvio(tabela) {
  return armazem.situacoes(tabela);
}
